import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { SignInResponse, UserStatus } from '../models/sign-in.model';
import {
  BackofficeStatus,
  QuizStatus,
  ValidationStatusResponse,
} from '../models/validation-status.model';
import { UserValidationService } from '../services/user-validation.service';

interface ValidationState {
  userStatus: UserStatus;
  quizStatus: QuizStatus;
  backofficeStatus: BackofficeStatus;
  fullyValidated: boolean;
}

function normalizeEnum<T extends string | null | undefined>(value: T): T {
  if (typeof value !== 'string') return value;
  return value.trim().toUpperCase() as T;
}

/**
 * Estado de validación hidratado desde:
 *  - `POST /auth/sign-in` al iniciar sesión.
 *  - `GET /user/validation-status` tras enviar el quiz.
 *
 * **`userStatus` es la fuente de verdad jerárquica** frente al payload extendido:
 *  - `V`: operativo como si `fullyValidated` fuera `true` (incluye legacy con
 *    quiz/backoffice en pendiente por datos viejos).
 *  - `NV`: usar `quizStatus`, `backofficeStatus` y `fullyValidated`.
 *  - Rechazo vía `R` o `backofficeStatus === 'REJECTED'`: prioridad en `isRejected`.
 *
 * Para operar según cache del front:
 *  - **Compra**: `canBuy()` / `effectiveFullyValidated()` (quiz + backoffice).
 *  - **Venta**: `canSell()` — solo exige quiz aprobado (o `userStatus === 'V'`);
 *    un rechazo de backoffice no bloquea vender saldos de bonos/campañas.
 */
@Injectable({ providedIn: 'root' })
export class UserValidationStateStore {
  private readonly userValidationService = inject(UserValidationService);

  private readonly state = signal<ValidationState | null>(null);

  readonly snapshot = this.state.asReadonly();
  readonly isLoaded = computed(() => this.state() !== null);

  readonly userStatus = computed(() => this.state()?.userStatus ?? null);
  readonly quizStatus = computed(() => this.state()?.quizStatus ?? null);
  readonly backofficeStatus = computed(
    () => this.state()?.backofficeStatus ?? null,
  );
  readonly fullyValidated = computed(
    () => this.state()?.fullyValidated ?? false,
  );

  readonly quizApproved = computed(() => this.quizStatus() === 'APPROVED');
  readonly backofficePending = computed(
    () => this.backofficeStatus() === 'PENDING',
  );
  readonly backofficeRejected = computed(
    () => this.backofficeStatus() === 'REJECTED',
  );

  /**
   * Verdadero si el usuario está rechazado por cualquier vía: `userStatus`
   * marcado como `R` (Rechazado) o `backofficeStatus` en `REJECTED`. Usar
   * este flag para decidir mostrar el modal "Gracias por tu interés" — cubre
   * los escenarios donde el back marca el rechazo en uno u otro campo.
   */
  readonly isRejected = computed(
    () =>
      this.userStatus() === 'R' || this.backofficeStatus() === 'REJECTED',
  );

  /**
   * Consolidado para UX y bloqueos proactivos: equivale a `fullyValidated` del
   * back cuando `userStatus === 'NV'`; con `V` es siempre `true` salvo rechazo.
   */
  readonly effectiveFullyValidated = computed(() => {
    if (!this.state()) return false;
    if (this.isRejected()) return false;
    if (this.userStatus() === 'V') return true;
    return this.fullyValidated();
  });

  /** Compra: requiere validación completa (quiz + backoffice). */
  readonly canBuy = computed(() => this.effectiveFullyValidated());

  /**
   * Venta: solo requiere quiz aprobado (o ser usuario legacy `V`).
   * El rechazo de backoffice no impide vender saldos existentes.
   */
  readonly canSell = computed(() => {
    if (!this.state()) return false;
    if (this.userStatus() === 'V') return true;
    return this.quizApproved();
  });

  setFromSignIn(response: SignInResponse): void {
    this.state.set({
      userStatus: normalizeEnum(response.userStatus) as UserStatus,
      quizStatus: normalizeEnum(response.quizStatus) as QuizStatus,
      backofficeStatus: normalizeEnum(
        response.backofficeStatus,
      ) as BackofficeStatus,
      fullyValidated: response.fullyValidated,
    });
  }

  setFromValidationStatus(response: ValidationStatusResponse): void {
    this.state.update((current) => ({
      // userStatus no viene en validation-status; preservamos el actual o
      // asumimos 'NV' si no había estado previo.
      userStatus: current?.userStatus ?? 'NV',
      quizStatus: normalizeEnum(response.quizStatus) as QuizStatus,
      backofficeStatus: normalizeEnum(
        response.backofficeStatus,
      ) as BackofficeStatus,
      fullyValidated: response.fullyValidated,
    }));
  }

  /**
   * Refresca el estado contra `GET /user/validation-status`. Útil tras enviar
   * el quiz, para reflejar las transiciones de backoffice más recientes.
   */
  refresh(): Observable<ValidationStatusResponse> {
    return this.userValidationService
      .getValidationStatus()
      .pipe(tap((response) => this.setFromValidationStatus(response)));
  }

  clear(): void {
    this.state.set(null);
  }
}
